/* ============================================================
   Nutriveska theme behaviour.

   No framework, no dependencies. Everything here is either an
   IntersectionObserver, a class toggle, or a fetch to Shopify's
   native /cart AJAX API.

   Motion is transform/opacity only, and every animation is
   disabled under prefers-reduced-motion by the stylesheet.
   ============================================================ */
(function () {
  "use strict";

  document.documentElement.classList.remove("no-js");

  var reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var money = window.NutriveskaMoneyFormat || "${{amount}}";

  /* ── Money formatting, matching the shop's own format string ── */
  function formatMoney(cents) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split(".");
    var withCommas = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return money
      .replace(/\{\{\s*amount\s*\}\}/, withCommas + "." + parts[1])
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, withCommas)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, withCommas + "," + parts[1]);
  }

  /* ── Focus trap, shared by both drawers ── */
  function trapFocus(container, onEscape) {
    var selector =
      'button:not([disabled]), a[href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])';

    function onKeydown(e) {
      if (e.key === "Escape") {
        onEscape();
        return;
      }
      if (e.key !== "Tab") return;
      var nodes = container.querySelectorAll(selector);
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeydown);
    return function release() {
      document.removeEventListener("keydown", onKeydown);
    };
  }

  /* ── Generic drawer ── */
  function Drawer(panel, overlay) {
    var releaseTrap = null;
    var lastFocused = null;
    var previousOverflow = "";

    return {
      panel: panel,
      open: function () {
        if (!panel) return;
        lastFocused = document.activeElement;
        previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        panel.classList.add("is-open");
        if (overlay) overlay.classList.add("is-open");
        panel.removeAttribute("aria-hidden");
        var close = panel.querySelector("[data-drawer-close]");
        if (close) close.focus();
        releaseTrap = trapFocus(panel, this.close.bind(this));
      },
      close: function () {
        if (!panel) return;
        document.body.style.overflow = previousOverflow;
        panel.classList.remove("is-open");
        if (overlay) overlay.classList.remove("is-open");
        panel.setAttribute("aria-hidden", "true");
        if (releaseTrap) releaseTrap();
        releaseTrap = null;
        if (lastFocused && lastFocused.focus) lastFocused.focus();
      },
    };
  }

  /* ── Mobile menu ── */
  var menuPanel = document.querySelector("[data-menu-drawer]");
  var menuOverlay = document.querySelector("[data-menu-overlay]");
  var menu = Drawer(menuPanel, menuOverlay);

  document.querySelectorAll("[data-menu-open]").forEach(function (btn) {
    btn.addEventListener("click", function () {
      menu.open();
    });
  });
  if (menuOverlay) menuOverlay.addEventListener("click", menu.close);
  if (menuPanel) {
    menuPanel.querySelectorAll("[data-drawer-close], a").forEach(function (el) {
      el.addEventListener("click", function () {
        // Let the link navigate; just release the lock.
        menu.close();
      });
    });
  }

  /* ── Cart drawer, backed by Shopify's native AJAX cart ── */
  var cartPanel = document.querySelector("[data-cart-drawer]");
  var cartOverlay = document.querySelector("[data-cart-overlay]");
  var cart = Drawer(cartPanel, cartOverlay);

  document.querySelectorAll("[data-cart-open]").forEach(function (btn) {
    btn.addEventListener("click", function (e) {
      e.preventDefault();
      cart.open();
    });
  });
  if (cartOverlay) cartOverlay.addEventListener("click", cart.close);
  if (cartPanel) {
    cartPanel.querySelectorAll("[data-drawer-close]").forEach(function (el) {
      el.addEventListener("click", cart.close);
    });
  }

  function setBusy(on) {
    if (!cartPanel) return;
    cartPanel.setAttribute("aria-busy", on ? "true" : "false");
    cartPanel.style.opacity = on ? "0.6" : "";
    cartPanel.style.pointerEvents = on ? "none" : "";
  }

  /* Re-render the drawer from the section rendering API so the markup
     always comes from Liquid — no duplicated templates in JS. */
  function refreshCart(openAfter) {
    return fetch(window.Shopify.routes.root + "?section_id=cart-drawer", {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then(function (r) {
        return r.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, "text/html");
        var fresh = parsed.querySelector("[data-cart-drawer]");
        var current = document.querySelector("[data-cart-drawer]");
        if (fresh && current) {
          var wasOpen = current.classList.contains("is-open");
          current.innerHTML = fresh.innerHTML;
          bindCartControls();
          if (wasOpen || openAfter) cart.open();
        }
        return updateCartCount();
      });
  }

  function updateCartCount() {
    return fetch(window.Shopify.routes.root + "cart.js", {
      headers: { "X-Requested-With": "XMLHttpRequest" },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (data) {
        document.querySelectorAll("[data-cart-count]").forEach(function (el) {
          el.textContent = data.item_count;
          el.hidden = data.item_count === 0;
        });
        document.querySelectorAll("[data-cart-trigger]").forEach(function (el) {
          el.setAttribute(
            "aria-label",
            "Cart, " + data.item_count + (data.item_count === 1 ? " item" : " items"),
          );
        });
        return data;
      });
  }

  function changeLine(key, quantity) {
    setBusy(true);
    return fetch(window.Shopify.routes.root + "cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity }),
    })
      .then(function () {
        return refreshCart(false);
      })
      .catch(function () {
        /* Network failure leaves the cart as-is rather than guessing. */
      })
      .finally(function () {
        setBusy(false);
      });
  }

  function bindCartControls() {
    document.querySelectorAll("[data-line-change]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        changeLine(btn.dataset.lineKey, parseInt(btn.dataset.lineChange, 10));
      });
    });
    document.querySelectorAll("[data-line-remove]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        changeLine(btn.dataset.lineKey, 0);
      });
    });
  }
  bindCartControls();

  /* ── Add to cart, from any product form ── */
  document.querySelectorAll("[data-product-form]").forEach(function (form) {
    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var button = form.querySelector('[type="submit"]');
      var original = button ? button.textContent : "";
      if (button) {
        button.setAttribute("aria-disabled", "true");
        button.textContent = "Adding…";
      }

      fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { Accept: "application/json" },
        body: new FormData(form),
      })
        .then(function (r) {
          return r.json().then(function (body) {
            if (!r.ok) throw new Error(body.description || body.message || "Could not add to cart");
            return body;
          });
        })
        .then(function () {
          return refreshCart(true);
        })
        .catch(function (err) {
          var error = form.querySelector("[data-form-error]");
          if (error) {
            error.textContent = err.message;
            error.hidden = false;
          }
        })
        .finally(function () {
          if (button) {
            button.removeAttribute("aria-disabled");
            button.textContent = original;
          }
        });
    });
  });

  /* ── Purchase options: one-time vs selling plan ── */
  document.querySelectorAll("[data-purchase-options]").forEach(function (group) {
    var planInput = group.querySelector("[data-selling-plan-input]");
    group.querySelectorAll('input[type="radio"]').forEach(function (radio) {
      radio.addEventListener("change", function () {
        // Empty value = one-time purchase; Shopify treats a blank
        // selling_plan as no plan.
        if (planInput) planInput.value = radio.dataset.sellingPlan || "";
        var priceEl = document.querySelector("[data-active-price]");
        var compareEl = document.querySelector("[data-active-compare]");
        if (priceEl && radio.dataset.price) {
          priceEl.textContent = formatMoney(parseInt(radio.dataset.price, 10));
        }
        if (compareEl) {
          if (radio.dataset.compare && radio.dataset.compare !== "0") {
            compareEl.textContent = formatMoney(parseInt(radio.dataset.compare, 10));
            compareEl.hidden = false;
          } else {
            compareEl.hidden = true;
          }
        }
      });
    });
  });

  /* ── Quantity steppers outside the cart ── */
  document.querySelectorAll("[data-qty]").forEach(function (widget) {
    var input = widget.querySelector("input");
    var output = widget.querySelector("output");
    widget.querySelectorAll("[data-qty-change]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var next = Math.max(1, parseInt(input.value, 10) + parseInt(btn.dataset.qtyChange, 10));
        input.value = next;
        if (output) output.textContent = next;
      });
    });
  });

  /* ── Product gallery ── */
  document.querySelectorAll("[data-gallery]").forEach(function (gallery) {
    var main = gallery.querySelector("[data-gallery-main]");
    gallery.querySelectorAll("[data-gallery-thumb]").forEach(function (thumb) {
      thumb.addEventListener("click", function () {
        if (main) {
          /* srcset wins over src, so it must be replaced as well —
             setting src alone leaves the previously selected candidate
             on screen and the click appears to do nothing. */
          if (thumb.dataset.srcset) {
            main.srcset = thumb.dataset.srcset;
          } else {
            main.removeAttribute("srcset");
          }
          main.src = thumb.dataset.full;
          main.alt = thumb.dataset.alt || "";
        }
        gallery.querySelectorAll("[data-gallery-thumb]").forEach(function (t) {
          t.setAttribute("aria-current", t === thumb ? "true" : "false");
        });
      });
    });
  });

  /* ── Sticky header shadow ── */
  var header = document.querySelector("[data-header]");
  if (header) {
    var onScroll = function () {
      header.classList.toggle("is-scrolled", window.scrollY > 80);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  /* ── Scroll reveal ── */
  var revealables = document.querySelectorAll(".reveal");
  if (reduceMotion || !("IntersectionObserver" in window)) {
    revealables.forEach(function (el) {
      el.classList.add("is-visible");
    });
  } else {
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          var el = entry.target;
          var delay = parseInt(el.dataset.revealDelay || "0", 10);
          setTimeout(function () {
            el.classList.add("is-visible");
          }, delay);
          observer.unobserve(el);
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.08 },
    );
    revealables.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ── Hero pointer tilt — restrained, pointer-only, compositor-only ── */
  var stage = document.querySelector("[data-stage]");
  if (stage && !reduceMotion && window.matchMedia("(pointer: fine)").matches) {
    var inner = stage.querySelector(".stage__inner");
    stage.addEventListener("pointermove", function (e) {
      var rect = stage.getBoundingClientRect();
      var x = (e.clientX - rect.left) / rect.width - 0.5;
      var y = (e.clientY - rect.top) / rect.height - 0.5;
      if (inner) inner.style.transform = "rotateY(" + x * 4 + "deg) rotateX(" + -y * 4 + "deg)";
    });
    stage.addEventListener("pointerleave", function () {
      if (inner) inner.style.transform = "";
    });
  }

  /* ── Video testimonials ──
     Sources are attached only as a card approaches the viewport, so an
     off-screen video costs nothing. Playback is muted and viewport-driven;
     exactly one video may carry sound, and a video that scrolls away is
     both paused and re-muted so nothing keeps talking off-screen. */
  var vtestVideos = [].slice.call(document.querySelectorAll("[data-vtest-video]"));
  if (vtestVideos.length) {
    var soundBtn = function (video) {
      return video.parentNode.querySelector("[data-vtest-sound]");
    };

    var showMuted = function (video) {
      var btn = soundBtn(video);
      if (btn) btn.setAttribute("aria-pressed", "false");
    };

    /* Promote data-src to src once, then let the element load. */
    var attach = function (video) {
      if (video.getAttribute("data-vtest-loaded")) return;
      video.setAttribute("data-vtest-loaded", "1");
      var sources = video.querySelectorAll("source[data-src]");
      for (var i = 0; i < sources.length; i++) {
        sources[i].src = sources[i].getAttribute("data-src");
        sources[i].removeAttribute("data-src");
      }
      video.load();
    };

    /* play() rejects when the browser declines autoplay. That is a normal
       outcome, not an error, so it is swallowed rather than logged. */
    var tryPlay = function (video) {
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    };

    var muteOthers = function (except) {
      vtestVideos.forEach(function (v) {
        if (v !== except && !v.muted) {
          v.muted = true;
          showMuted(v);
        }
      });
    };

    vtestVideos.forEach(function (video) {
      var btn = soundBtn(video);
      if (!btn) return;
      btn.addEventListener("click", function () {
        var turningOn = video.muted;
        if (turningOn) {
          muteOthers(video);
          attach(video);
          video.muted = false;
          tryPlay(video);
        } else {
          video.muted = true;
        }
        btn.setAttribute("aria-pressed", turningOn ? "true" : "false");
      });
    });

    if (reduceMotion || !("IntersectionObserver" in window)) {
      /* No autoplay here. Give people the native controls instead, so the
         videos are still watchable. */
      vtestVideos.forEach(function (video) {
        video.controls = true;
        video.setAttribute("preload", "metadata");
        attach(video);
      });
    } else {
      var vtestObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var video = entry.target;
            if (entry.isIntersecting) {
              attach(video);
              tryPlay(video);
            } else {
              video.pause();
              if (!video.muted) {
                video.muted = true;
                showMuted(video);
              }
            }
          });
        },
        { threshold: 0.5 },
      );
      vtestVideos.forEach(function (video) {
        vtestObserver.observe(video);
      });
    }
  }

  updateCartCount();
})();
